import { useEffect, useReducer } from "react";

import { sendRequest } from "../../common/http";
import MissionAndOutcomes from "./mission-and-outcomes/MissionAndOutcomes";
import "../common/Insights.scss";
import ProgramILOMappings from "./ProgramILOMappings";
import TopProgramsByCatalog from "./top-programs-by-catalog/TopProgramsByCatalog";
import translate from "@/i18n/translate";
import { useContext } from "react";
import { AppContext, AppContextInterface } from "../../contexts/AppContext";
import { getLoaderStatus } from "../common/helper";
import {
  Institution,
  Lead,
  ReportingYear
} from "../common/interfaces/Interface";
import CurriculumProposals from "./CurriculumProposals";
interface loadedComponents {
  programs: boolean;
  organizations: boolean;
  courses: boolean;
  programILOMappings: boolean;
  curriculumProposals: boolean;
  topProgramsByCatalog: boolean;
}

const allComponentsLoaded = (loadedComponents: loadedComponents) => {
  return (
    loadedComponents.programs &&
    loadedComponents.organizations &&
    loadedComponents.courses &&
    loadedComponents.programILOMappings &&
    loadedComponents.curriculumProposals &&
    loadedComponents.topProgramsByCatalog
  );
};
interface InstitutionState {
  loadedComponents: loadedComponents;
  csMappingId: null | string;
  showCurriculumProposals: boolean;
}

interface InstitutionAction {
  type: string;
  component: string;
  csMappingId: null | string;
  data: any;
}
const defaultInstitutionAction = {
  type: "",
  component: "",
  csMappingId: null,
  data: {}
};

export interface StrategyProps {
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  ryApiError: boolean;
  setStrategyTab: () => void;
}

export default function Strategy({
  reportingYears,
  defaultReportingYear,
  ryApiError,
  setStrategyTab
}: StrategyProps) {
  const { membership, setleads, institution, setOutcomesTerminology } =
    useContext<AppContextInterface>(AppContext);

  const initialState = {
    loadedComponents: {
      programs: false,
      organizations: false,
      courses: false,
      programILOMappings: false,
      curriculumProposals: false,
      topProgramsByCatalog: false
    },
    csMappingId: null,
    showCurriculumProposals: false
  };

  const reducer = (state: InstitutionState, action: InstitutionAction) => {
    switch (action.type) {
      case "setLoadedComponent":
        const component = action.component;
        let loadedComponents = state.loadedComponents;
        loadedComponents[component] = true;
        return { ...state, loadedComponents: loadedComponents };
      case "setCsMappingId":
        return {
          ...state,
          csMappingId: action.csMappingId
        };
      case "setShowCurriculumProposals":
        return {
          ...state,
          showCurriculumProposals: action.data.showCurriculumProposals
        };
      case "resetLoadedComponent":
        const newComponent = action.component;
        let newloadedComponents = state.loadedComponents;
        newloadedComponents[newComponent] = false;
        return { ...state, loadedComponents: newloadedComponents };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (institution?.isPssIntegrated) {
      const applyLeadsResult = (result: Lead) => {
        setleads(result);
      };
      sendRequest({ uri: "/leads", camelize: true }, applyLeadsResult);

      const applyOutcomesTerminologyResult = (result: any) => {
        setOutcomesTerminology(result);
      };
      sendRequest({ uri: "/outcomes-terminology", camelize: true }, applyOutcomesTerminologyResult);
    }
  }, [institution]);

  const onComponentLoad = (component: string) => {
    dispatch({
      ...defaultInstitutionAction,
      type: "setLoadedComponent",
      component: component
    });
  };
  const resetLoadedComponent = (component: string) => {
    dispatch({
      ...defaultInstitutionAction,
      type: "resetLoadedComponent",
      component: component
    });
  };

  useEffect(() => {
    const liveRegion = document.querySelector(".institution-status");
    getLoaderStatus(!allComponentsLoaded(state.loadedComponents), liveRegion);
  }, [allComponentsLoaded(state.loadedComponents)]);

  useEffect(() => {
    if (institution && institution.salesforceId !== "") {
      const applyResult = (result: any) => {
        if (!institution.isPssIntegrated && result.mappingId) {
          setStrategyTab();
        }

        dispatch({
          ...defaultInstitutionAction,
          type: "setCsMappingId",
          csMappingId: result.mappingId ? result.mappingId : null
        });
      };

      sendRequest(
        {
          uri: `/cs/institution_mapping`,
          camelize: true
        },
        applyResult
      );
    }
  }, [institution && institution.salesforceId]);

  useEffect(() => {
    if (state.csMappingId) {
      const applyResult = (result: any) => {
        dispatch({
          ...defaultInstitutionAction,
          type: "setShowCurriculumProposals",
          data: result
        });
      };

      sendRequest(
        {
          uri: `/cs/institution_products?mapping_id=${state.csMappingId}`,
          camelize: true
        },
        applyResult
      );
    }
  }, [state.csMappingId]);

  if (institution?.isPssIntegrated || state.csMappingId) {
    return (
      <StrategyWidgets
        institution={institution}
        csMappingId={state.csMappingId}
        showCurriculumProposals={state.showCurriculumProposals}
        reportingYears={reportingYears}
        defaultReportingYear={defaultReportingYear}
        onComponentLoad={onComponentLoad}
        resetLoadedComponent={resetLoadedComponent}
        ryApiError={ryApiError}
      />
    );
  } else {
    return <></>;
  }
}
interface StrategyWidgetsProps {
  institution: Institution | null;
  csMappingId: string | null;
  showCurriculumProposals: boolean;
  reportingYears: ReportingYear[] | null;
  defaultReportingYear: ReportingYear;
  onComponentLoad: (component: string) => void;
  resetLoadedComponent: (component: string) => void;
  ryApiError: boolean;
}
const StrategyWidgets = (props: StrategyWidgetsProps) => {
  return (
    <section
      className="body wm-grid tab-body-home"
      id="section_insights"
    >
      <div>
        <span
          className="sr-only institution-status"
          role="status"
          aria-live="assertive"
        ></span>
        <section className="insights">
          <div className="institution-name">
            <h3 className="helper-label helper-default-margin">
              {translate("app.strategyLabel")}
            </h3>
          </div>
          <div className="wm-grid-row">
            {props.institution?.isPssIntegrated ? (
              <>
                <div
                  className={
                    props.csMappingId
                      ? "grid-container wm-grid-col-6 wm-grid-col-md-10"
                      : "grid-container wm-grid-col-12 wm-grid-col-md-10"
                  }
                >
                  <MissionAndOutcomes onComponentLoad={props.onComponentLoad} />
                </div>
                <div
                  className={
                    props.csMappingId
                      ? "grid-container wm-grid-col-6 wm-grid-col-md-10"
                      : "grid-container wm-grid-col-12 wm-grid-col-md-10"
                  }
                >
                  <ProgramILOMappings
                    onComponentLoad={props.onComponentLoad}
                    reportingYears={props.reportingYears}
                  />
                </div>
              </>
            ) : (
              <></>
            )}
            {props.csMappingId && props.showCurriculumProposals && (
              <div className="grid-container wm-grid-col-6 wm-grid-col-md-10">
                <CurriculumProposals
                  reportingYears={props.reportingYears}
                  defaultReportingYear={props.defaultReportingYear}
                  mappingId={props.csMappingId}
                  onComponentLoad={props.onComponentLoad}
                  resetLoadedComponent={props.resetLoadedComponent}
                  ryApiError={props.ryApiError}
                />
              </div>
            )}
            {props.csMappingId && (
              <div
                className={
                  props.showCurriculumProposals
                    ? "grid-container wm-grid-col-6 wm-grid-col-md-10"
                    : "grid-container wm-grid-col-12 wm-grid-col-md-10"
                }
              >
                <TopProgramsByCatalog
                  mappingId={props.csMappingId}
                  onComponentLoad={props.onComponentLoad}
                />
              </div>
            )}
          </div>
        </section>
      </div>
    </section>
  );
};
